import { CobaltButton } from '@cae/cobalt-react/components/button';
import {
  CobaltTable,
  CobaltTableBody,
  CobaltTableCell,
  CobaltTableHead,
  CobaltTableRow,
} from '@cae/cobalt-react/components/table';
import { CobaltIconDownloadOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDownloadOutline';
import { CobaltIconFileSolid } from '@cae/cobalt-react/icons/solid/CobaltIconFileSolid';
import { useDownloadDocument } from '@features/documents/hooks/use-download-document/useDownloadDocument';
import * as React from 'react';

import type { DocumentsSearchResultsTableProps } from './DocumentsSearchResultsTable.types';

import styles from './DocumentsSearchResultsTable.module.css';
import { getLocationName } from './DocumentsSearchResultsTable.utils';

const DocumentsSearchResultsTable = React.memo(
  ({
    documents,
    organizationId,
    canDownload,
  }: Readonly<DocumentsSearchResultsTableProps>): React.ReactElement => {
    const { download, isDocumentDownloading } = useDownloadDocument();

    const fileDocuments = React.useMemo(
      () => documents.filter(doc => doc.isFile),
      [documents]
    );

    const handleDownload = React.useCallback(
      (documentId: string): void => {
        download(documentId, organizationId);
      },
      [download, organizationId]
    );

    return (
      <CobaltTable
        size="x-large"
        className={styles.searchResultsTable}
        data-testid="documents-search-results-table"
      >
        <CobaltTableHead>
          <CobaltTableRow>
            <CobaltTableCell header className={styles.dataColumn}>
              Name
            </CobaltTableCell>
            <CobaltTableCell header className={styles.dataColumn}>
              Location
            </CobaltTableCell>
            <CobaltTableCell header className={styles.downloadCell} />
          </CobaltTableRow>
        </CobaltTableHead>
        <CobaltTableBody>
          {fileDocuments.map(doc => (
            <CobaltTableRow key={doc.id} interactive>
              <CobaltTableCell className={styles.dataColumn}>
                <span className={styles.nameCell}>
                  <CobaltIconFileSolid
                    size="medium"
                    className={styles.fileIcon}
                  />
                  {doc.name}
                </span>
              </CobaltTableCell>
              <CobaltTableCell className={styles.dataColumn}>
                {getLocationName(doc)}
              </CobaltTableCell>
              <CobaltTableCell className={styles.downloadCell}>
                {canDownload && (
                  <CobaltButton
                    variant="ghost"
                    size="small"
                    aria-label={`Download ${doc.name}`}
                    onClick={() => handleDownload(doc.id)}
                    disabled={isDocumentDownloading(doc.id)}
                    pending={isDocumentDownloading(doc.id)}
                  >
                    <CobaltIconDownloadOutline />
                  </CobaltButton>
                )}
              </CobaltTableCell>
            </CobaltTableRow>
          ))}
        </CobaltTableBody>
      </CobaltTable>
    );
  }
);

DocumentsSearchResultsTable.displayName = 'DocumentsSearchResultsTable';

export { DocumentsSearchResultsTable };
